import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import { ScrollArea } from "@/components/ui/shadcn/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/shadcn/sheet";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  LucideIcon,
  Menu,
  PanelLeftOpen,
  PanelRightOpen,
} from "lucide-react";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router";

export interface WorkspaceNavItem {
  label: string;
  description?: string;
  path: string;
  Icon: LucideIcon;
  matchPaths?: string[];
}

interface CommandWorkspaceLayoutProps {
  navItems: WorkspaceNavItem[];
  rightPanel?: ReactNode;
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  navFooter?: (collapsed: boolean) => ReactNode;
  topActions?: ReactNode;
  unreadCount?: number;
}

export const CommandWorkspaceLayout = ({
  navItems,
  rightPanel,
  children,
  className,
  title,
  subtitle,
  navFooter,
  topActions,
  unreadCount,
}: CommandWorkspaceLayoutProps) => {
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileRightOpen, setMobileRightOpen] = useState(false);

  const computedTitle = useMemo(() => title ?? "Workspace", [title]);

  const renderNavLinks = useCallback(
    (options?: { showDetails?: boolean; onNavigate?: () => void }) => {
      const showDetails = options?.showDetails ?? true;
      const handleNavigate = options?.onNavigate;

      return navItems.map(({ label, description, path, Icon, matchPaths }) => {
        const isActiveFunc = matchPaths
          ? ({ isActive }: { isActive: boolean }) => {
              if (isActive) return true;
              const currentPath = window.location.pathname;
              return matchPaths.some((pattern) => {
                const regex = new RegExp(
                  pattern.replace(/:\w+/g, "[^/]+").replace(/\//g, "\\/"),
                );
                return regex.test(currentPath);
              });
            }
          : undefined;

        return (
          <NavLink
            key={path}
            to={path}
            end={!matchPaths}
            onClick={handleNavigate}
            className={({ isActive }) => {
              const active = isActiveFunc
                ? isActiveFunc({ isActive })
                : isActive;
              return cn(
                "group relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200",
                "text-gray-900 hover:bg-bg-dark hover:text-primary hover:shadow-[var(--shadow-depth-sm)]",
                active &&
                  "bg-bg-light text-primary shadow-[var(--shadow-depth-md)] ring-1 ring-primary/10 hover:bg-bg-light",
              );
            }}
          >
            {({ isActive }) => {
              const active = isActiveFunc
                ? isActiveFunc({ isActive })
                : isActive;
              return (
                <>
                  <Icon
                    className={cn(
                      "h-5 w-5 text-primary/70 transition group-hover:text-primary",
                      active && "text-primary",
                    )}
                  />
                  {showDetails && (
                    <div className="flex flex-col">
                      <span>{label}</span>
                      {description && (
                        <span className="text-xs font-normal text-gray-700">
                          {description}
                        </span>
                      )}
                    </div>
                  )}
                </>
              );
            }}
          </NavLink>
        );
      });
    },
    [navItems],
  );

  return (
    <div className={cn("flex h-[100dvh] bg-bg-dark", className)}>
      {/* Mobile navigation sheet */}
      <Sheet
        open={mobileNavOpen}
        onOpenChange={(open) => {
          setMobileNavOpen(open);
          if (open) setMobileRightOpen(false);
        }}
      >
        <SheetContent
          side="left"
          className="w-[85vw] max-w-sm bg-bg-panel p-0 shadow-[0_4px_14px_rgba(15,18,25,0.12),0_1px_2px_rgba(15,18,25,0.06)] ring-1 ring-gray-300/60"
        >
          <SheetHeader className="px-6 py-4 text-left">
            <SheetTitle className="text-base font-semibold text-gray-900">
              Navigation
            </SheetTitle>
          </SheetHeader>
          <div className="flex h-[calc(100dvh-4rem)] flex-col">
            <ScrollArea className="flex-1 px-4 pb-6">
              <nav className="flex flex-col gap-1">
                {renderNavLinks({
                  showDetails: true,
                  onNavigate: () => setMobileNavOpen(false),
                })}
              </nav>
            </ScrollArea>

            {navFooter && (
              <div className="border-t border-gray-300/40 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm text-gray-700">
                {navFooter(false)}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile insights sheet */}
      {rightPanel && (
        <Sheet
          open={mobileRightOpen}
          onOpenChange={(open) => {
            setMobileRightOpen(open);
            if (open) setMobileNavOpen(false);
          }}
        >
          <SheetContent
            side="right"
            className="w-[85vw] max-w-sm bg-bg-panel p-0 shadow-[0_4px_14px_rgba(15,18,25,0.12),0_1px_2px_rgba(15,18,25,0.06)] ring-1 ring-gray-300/60"
          >
            <SheetHeader className="px-6 py-4 text-left">
              <SheetTitle className="text-base font-semibold text-gray-900">
                Notifications & reminders
              </SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100dvh-4rem)] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {rightPanel}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      <div className="lg:hidden">
        <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between bg-bg-panel/95 px-4 py-3 shadow-[var(--shadow-depth-sm)] backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="z-50 h-11 w-11 rounded-full bg-bg-dark text-gray-700 shadow-[var(--shadow-depth-sm)] backdrop-blur transition hover:bg-bg-light hover:text-primary hover:shadow-[var(--shadow-depth-md)]"
            onClick={() => {
              setMobileNavOpen(true);
              setMobileRightOpen(false);
            }}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <Logo variant="It" className="h-6 w-auto shrink-0 object-contain" />

          {rightPanel ? (
            <Button
              variant="ghost"
              size="icon"
              className="z-50 h-11 w-11 rounded-full bg-bg-dark text-gray-700 shadow-[var(--shadow-depth-sm)] backdrop-blur transition hover:bg-bg-light hover:text-primary hover:shadow-[var(--shadow-depth-md)]"
              onClick={() => {
                setMobileRightOpen(true);
                setMobileNavOpen(false);
              }}
            >
              <PanelRightOpen className="h-5 w-5" />
            </Button>
          ) : (
            <div className="h-10 w-10" />
          )}
        </div>

        <div className="h-16" />
      </div>

      <aside
        className={cn(
          "relative hidden h-full flex-col rounded-r-2xl bg-bg-panel shadow-[0_4px_14px_rgba(15,18,25,0.12),0_1px_2px_rgba(15,18,25,0.06)] ring-1 ring-gray-300/60 transition-[width] duration-300 lg:flex",
          navCollapsed ? "w-16" : "w-64",
        )}
      >
        <div className="relative flex min-h-[148px] items-start justify-between px-4 py-5">
          {!navCollapsed ? (
            <div className="flex flex-col gap-2">
              <Logo
                variant="A"
                className="h-5 max-w-[110px] w-auto shrink-0 object-contain"
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                Command center
              </span>
              <span className="text-lg font-semibold text-gray-900">
                {computedTitle}
              </span>
              {subtitle && (
                <span className="text-xs text-gray-700">{subtitle}</span>
              )}
            </div>
          ) : (
            <Logo variant="It" className="h-6 w-auto shrink-0 object-contain" />
          )}

          <div className="absolute -right-3 top-6 z-40">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full bg-bg-dark text-gray-700 shadow-[var(--shadow-depth-sm)] transition hover:bg-bg-light hover:text-primary hover:shadow-[var(--shadow-depth-md)]"
              onClick={() => {
                setNavCollapsed((prev) => {
                  const newValue = !prev;
                  // If expanding nav (newValue = false), collapse right panel
                  if (!newValue) {
                    setRightCollapsed(true);
                  }
                  return newValue;
                });
              }}
            >
              {navCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 flex-col">
          <ScrollArea className="flex-1 px-3 pb-6">
            <nav className="flex flex-col gap-1">
              {renderNavLinks({ showDetails: !navCollapsed })}
            </nav>
          </ScrollArea>

          {navFooter && (
            <div className="border-t border-gray-4 px-3 py-4 text-sm text-gray-600">
              {navFooter(navCollapsed)}
            </div>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:flex-row">
        <main className="flex min-h-0 flex-1 flex-col">
          {topActions && (
            <div className="hidden lg:flex items-center justify-end px-8 pt-6">
              {topActions}
            </div>
          )}
          <div className="flex-1 overflow-y-auto bg-bg-dark pt-[75px] sm:pt-[72px] lg:pt-10">
            {children}
          </div>
        </main>

        {rightPanel && (
          <aside
            className={cn(
              "relative hidden h-full rounded-l-2xl bg-bg-panel shadow-[0_4px_14px_rgba(15,18,25,0.12),0_1px_2px_rgba(15,18,25,0.06)] ring-1 ring-gray-300/60 transition-[width] duration-300 xl:flex",
              rightCollapsed ? "w-16" : "w-[300px]",
            )}
          >
            <div className="absolute -left-3 top-6 z-40">
              <Button
                variant="ghost"
                size="icon"
                className="z-40 h-9 w-9 rounded-full bg-bg-dark text-gray-700 shadow-[var(--shadow-depth-sm)] transition hover:bg-bg-light hover:text-primary hover:shadow-[var(--shadow-depth-md)]"
                onClick={() => {
                  setRightCollapsed((prev) => {
                    const newValue = !prev;
                    // If expanding right panel (newValue = false), collapse nav
                    if (!newValue) {
                      setNavCollapsed(true);
                    }
                    return newValue;
                  });
                }}
              >
                <PanelLeftOpen
                  className={cn(
                    "h-4 w-4 transition-transform",
                    rightCollapsed && "rotate-180",
                  )}
                />
              </Button>
            </div>

            <ScrollArea className="flex-1 px-7 py-6">
              {rightCollapsed ? (
                unreadCount !== undefined && unreadCount > 0 ? (
                  <div className="flex h-full items-center justify-center text-2xl font-semibold text-primary">
                    {unreadCount}
                  </div>
                ) : null
              ) : (
                rightPanel
              )}
            </ScrollArea>
          </aside>
        )}
      </div>
    </div>
  );
};
